import { Controller } from '@hotwired/stimulus';

// Keep the panel this far away from the viewport edges.
const GUTTER = 8;

// Generic dropdown menu: toggles a panel and closes on outside click or Escape.
// The panel is laid out in the viewport (`position: fixed`, see
// ui/templates/menu.css) so it escapes clipping ancestors such as the
// `overflow-hidden` of `.ds-card`. This controller only feeds it coordinates,
// through the `--ds-menu-x` / `--ds-menu-y` custom properties.
export default class extends Controller {
  static targets = ['button', 'panel'];

  static values = { offset: { type: Number, default: 6 } };

  connect() {
    this.onOutside = this.onOutside.bind(this);
    this.onKey = this.onKey.bind(this);
    this.onViewportChange = this.onViewportChange.bind(this);
    this.syncExpanded();
  }

  disconnect() {
    this.stopListening();
  }

  toggle(event) {
    event.stopPropagation();
    this.isOpen ? this.close() : this.open();
  }

  open() {
    this.panelTarget.hidden = false;
    this.element.classList.add('is-open');
    this.syncExpanded();
    this.position();
    document.addEventListener('mousedown', this.onOutside);
    document.addEventListener('keydown', this.onKey);
    document.addEventListener('scroll', this.onViewportChange, {
      capture: true,
      passive: true,
    });
    window.addEventListener('resize', this.onViewportChange);
  }

  close() {
    if (!this.isOpen) return;
    this.panelTarget.hidden = true;
    this.element.classList.remove('is-open');
    this.syncExpanded();
    this.clearPosition();
    this.stopListening();
  }

  stopListening() {
    if (this.frame) {
      cancelAnimationFrame(this.frame);
      this.frame = null;
    }
    document.removeEventListener('mousedown', this.onOutside);
    document.removeEventListener('keydown', this.onKey);
    document.removeEventListener('scroll', this.onViewportChange, {
      capture: true,
    });
    window.removeEventListener('resize', this.onViewportChange);
  }

  // Anchor the panel below the button, aligned on its right edge, flipping
  // above when the space below is too short. Panel dimensions are read from
  // offsetWidth / offsetHeight: the pop-in animation scales the panel, which
  // would skew a getBoundingClientRect measurement.
  position() {
    const anchor = this.buttonTarget.getBoundingClientRect();
    const { offsetWidth: width, offsetHeight: height } = this.panelTarget;
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    let top = anchor.bottom + this.offsetValue;

    if (top + height > viewportHeight - GUTTER) {
      const above = anchor.top - this.offsetValue - height;
      top = above >= GUTTER
        ? above
        : Math.max(GUTTER, viewportHeight - GUTTER - height);
    }

    const left = Math.min(
      Math.max(GUTTER, anchor.right - width),
      Math.max(GUTTER, viewportWidth - GUTTER - width),
    );

    this.panelTarget.style.setProperty('--ds-menu-x', `${Math.round(left)}px`);
    this.panelTarget.style.setProperty('--ds-menu-y', `${Math.round(top)}px`);
  }

  clearPosition() {
    this.panelTarget.style.removeProperty('--ds-menu-x');
    this.panelTarget.style.removeProperty('--ds-menu-y');
  }

  // Scrolling or resizing moves the button: follow it, and close once it has
  // left the viewport, where the panel would otherwise float on its own.
  onViewportChange() {
    if (this.frame) return;

    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      if (!this.isOpen) return;

      const anchor = this.buttonTarget.getBoundingClientRect();
      if (anchor.bottom < 0 || anchor.top > window.innerHeight) {
        this.close();
        return;
      }

      this.position();
    });
  }

  onOutside(event) {
    if (!this.element.contains(event.target)) this.close();
  }

  onKey(event) {
    if (event.key === 'Escape') this.close();
  }

  syncExpanded() {
    if (!this.hasButtonTarget) return;

    this.buttonTarget.setAttribute('aria-expanded', String(this.isOpen));
  }

  get isOpen() {
    return !this.panelTarget.hidden;
  }
}
